/* Optional extension for xFiltersAndSortby; legacy collection/search controls
   continue to use the original controller. DOM resources live outside Alpine's
   reactive proxy so native Animation/AbortController methods keep their receiver. */
window.MWAdaptiveCollection = (sectionId, defaultColumns, options) => {
  let rootElement;
  let lifecycle, request, observer, animations = [], transition = 0, sequence = 0;
  let tooltipTimer, tooltipAnchor, tooltipDragging = false, lastHTML = '', retryParams = '';
  let layoutReady = false, scrollLock = null;
  let loadingTimer, pendingGridHeight = null;
  // The unfiltered inventory belongs to this collection, never to one result page.
  let quickInventory = [], quickRequest = null, quickResolved = false;
  const storedNumber = (key, fallback, min, max) => {
    try {
      const value = Number(localStorage.getItem(key));
      return Number.isInteger(value) && value >= min && value <= max ? value : fallback;
    } catch (_) { return fallback; }
  };
  const save = (key, value) => { try { localStorage.setItem(key, value); } catch (_) { /* Private browsing can deny storage. */ } };
  return {
    adaptiveMode: true,
    activeAdaptiveFilter: '',
    quickFilterKeys: [],
    groupedFilters: false,
    adaptiveError: false,
    showAdaptiveProgress: false,
    mobileColumns: Number(options.mobileColumns) || 2,
    priceDrafts: {},
    adaptivePendingParams: null,
    sheetOpen: false,
    mobileGroups: {},
    appliedFilterCount: 0,
    _root() { return rootElement || this.$root; },
    init() {
      // Event expressions have an element-scoped $root magic. Hold the actual
      // component root before replacing the event target during an AJAX update.
      rootElement = this.$root;
      lifecycle = new AbortController();
      const signal = lifecycle.signal;
      this.desktopColumns = storedNumber('product-grid-columns', Number(defaultColumns) || 4, 2, 5);
      this.mobileColumns = storedNumber('product-grid-columns-mobile', Number(options.mobileColumns) || 2, 1, 2);
      const locationParams = this._readLocationSearchParams();
      // The initial HTML reflects query parameters, never the URL fragment.
      this.searchParamsPrev = new URLSearchParams(window.location.search).toString();
      this.searchParamsInitial = locationParams;
      this._initLayout();
      const resize = () => {
        this._initLayout();
        this.measureAdaptive();
        this.setFilterHeaderHeight();
        this.hideAdaptiveTooltip();
        this.closeAdaptiveSort(false);
      };
      window.addEventListener('resize', resize, { signal });
      const historyChange = () => {
        const params = this._readLocationSearchParams();
        if (params === (this.adaptivePendingParams ?? this.searchParamsPrev)) return;
        this.priceDrafts = {};
        this._renderPage(params, false);
      };
      window.addEventListener('popstate', historyChange, { signal });
      if (this.useHashFilterUrls) window.addEventListener('hashchange', historyChange, { signal });
      this._root().addEventListener('click', event => {
        const link = event.target.closest('#ProductGridContainer .pagination a[href]');
        if (!link || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        const target = new URL(link.href, window.location.href);
        const params = new URLSearchParams(target.search);
        this._deleteHashParams(params);
        this._hashParamsFromCombined(this.searchParamsPrev).forEach((value, key) => params.append(key, value));
        this._renderPage(params.toString());
      }, { signal });
      document.addEventListener('scroll', event => {
        this.hideAdaptiveTooltip();
        if (!this._root().querySelector('[data-sort-menu]')?.contains(event.target)) this.closeAdaptiveSort(false);
      }, { capture: true, signal });
      document.addEventListener('pointerdown', event => {
        if (!this._root().querySelector('[data-adaptive-sort-zone]')?.contains(event.target)) this.closeAdaptiveSort(false);
      }, { signal });
      window.addEventListener('pointerup', () => this.endAdaptiveDrag(), { signal });
      this.$nextTick(() => {
        lastHTML = this._root().outerHTML;
        this._initQuickFilters();
        this._syncAdaptiveScreens();
        this._restoreAdaptiveDrafts();
        this._syncAdaptiveMetadata();
        this._syncAdaptiveSort();
        this.measureAdaptive();
        this.setFilterHeaderHeight();
        this._prepareHashPaginationLinks();
        observer = new ResizeObserver(() => this.measureAdaptive());
        observer.observe(this._root().querySelector('[data-toolbar-scroll]'));
        document.fonts?.ready.then(() => { if (!signal.aborted) this.measureAdaptive(); });
        const hash = this._hashParamsFromHash();
        if (this.useHashFilterUrls && (hash.toString() || this._hashParamsFromCombined(window.location.search).toString())) {
          this._updateURLHash(locationParams, true);
        }
        if (this.useHashFilterUrls && hash.toString()) this._renderPage(locationParams, false);
      });
    },
    destroy() {
      ++sequence;
      this.loading = false;
      this._finishAdaptiveLoading();
      this.closeAdaptiveSheet(false);
      this.closeAdaptiveSort(false);
      lifecycle?.abort(); request?.abort(); quickRequest?.abort(); observer?.disconnect();
      animations.forEach(animation => animation.cancel());
      clearTimeout(tooltipTimer);
    },
    _initLayout() {
      const desktop = window.innerWidth >= 768;
      if (!layoutReady || desktop !== this.isDesktop) {
        const focusInSheet = this._root().querySelector('[data-filter-sheet]')?.contains(document.activeElement);
        this.closeAdaptiveSort(false);
        if (desktop) this.closeAdaptiveSheet(false);
        ++transition;
        animations.forEach(animation => animation.cancel());
        this.isDesktop = desktop;
        this._moveAdaptiveFilters();
        this._syncAdaptiveScreens();
        layoutReady = true;
        if (desktop && focusInSheet) {
          const screen = this._adaptiveScreens().find(item => item.dataset.filterScreen === this.activeAdaptiveFilter);
          screen?.querySelector(this.activeAdaptiveFilter ? '.mwact-back' : '[data-category]')?.focus({ preventScroll: true });
        }
      }
      this._applyGridColumns();
    },
    _moveAdaptiveFilters() {
      const filters = this._root().querySelector('[data-adaptive-filters]');
      const destination = this._root().querySelector(this.isDesktop ? '[data-adaptive-filter-slot]' : '[data-sheet-body]');
      if (filters && destination && filters.parentElement !== destination) {
        // Preserve inputs and Alpine bindings when changing presentation.
        Alpine.mutateDom(() => destination.append(filters));
      }
    },
    openAdaptiveSheet() {
      if (this.isDesktop) return;
      const dialog = this._root().querySelector('[data-filter-sheet]');
      if (!dialog || dialog.open) return;
      this.closeAdaptiveSort(false);
      this.hideAdaptiveTooltip();
      this._moveAdaptiveFilters();
      this._syncAdaptiveScreens();
      scrollLock = [document.documentElement.style.overflow, document.body.style.overflow];
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      this.sheetOpen = true;
      dialog.showModal();
      this._syncCountAnnouncements();
      dialog.querySelector('.mwact-sheet-close').focus({ preventScroll: true });
    },
    closeAdaptiveSheet(restoreFocus = true) {
      const dialog = this._root()?.querySelector('[data-filter-sheet]');
      const wasOpen = this.sheetOpen || dialog?.open;
      this.sheetOpen = false;
      if (dialog?.open) dialog.close();
      if (scrollLock) {
        [document.documentElement.style.overflow, document.body.style.overflow] = scrollLock;
        scrollLock = null;
      }
      this._syncCountAnnouncements();
      if (wasOpen && restoreFocus && !this.isDesktop) this._root().querySelector('[data-mobile-filter-trigger]')?.focus({ preventScroll: true });
    },
    toggleAdaptiveGroup(key) {
      this.mobileGroups[key] = this.mobileGroups[key] === false;
      this._syncAdaptiveScreens();
    },
    trapAdaptiveSheetFocus(event) {
      const dialog = this._root().querySelector('[data-filter-sheet]');
      const controls = Array.from(dialog.querySelectorAll('button, input, select, a[href], [tabindex]')).filter(control => !control.disabled && control.tabIndex >= 0 && !control.closest('[hidden], [inert]') && control.getClientRects().length);
      const first = controls[0], last = controls[controls.length - 1];
      if (!first) return;
      if (event.shiftKey && (document.activeElement === first || !controls.includes(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !controls.includes(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    },
    _syncCountAnnouncements() {
      this._root()?.querySelector('[data-adaptive-count]')?.setAttribute('aria-live', this.sheetOpen ? 'off' : 'polite');
      this._root()?.querySelector('[data-sheet-count]')?.setAttribute('aria-live', this.sheetOpen ? 'polite' : 'off');
    },
    _syncAdaptiveMetadata(parsed) {
      const shell = this._root().querySelector('[data-adaptive-toolbar]');
      const incoming = parsed?.querySelector('[data-adaptive-toolbar]') || shell;
      this.appliedFilterCount = Number(incoming.dataset.activeCount) || 0;
      shell.dataset.activeCount = incoming.dataset.activeCount;
      shell.dataset.resultCount = incoming.dataset.resultCount;
      const value = this._root().querySelector('.mwact-result-value');
      if (value) value.textContent = new Intl.NumberFormat(shell.dataset.locale).format(Number(incoming.dataset.resultCount) || 0);
      const badge = this._root().querySelector('[data-mobile-filter-count]');
      if (badge) { badge.textContent = this.appliedFilterCount; badge.hidden = this.appliedFilterCount === 0; }
      const trigger = this._root().querySelector('[data-mobile-filter-trigger]');
      if (trigger) trigger.hidden = !this._root().querySelector('[data-adaptive-filters]:not([hidden])');
      this._syncCountAnnouncements();
    },
    _syncAdaptiveSort() {
      const select = this._root().querySelector('[data-adaptive-sort]');
      if (!select) return;
      this.currentSort = select.value;
      this.currentSortLabel = select.options[select.selectedIndex]?.text || '';
      const trigger = this._root().querySelector('[data-sort-trigger]');
      trigger?.setAttribute('aria-label', `${trigger.dataset.sortLabel}: ${this.currentSortLabel}`);
      this._root().querySelectorAll('[data-sort-option]').forEach(option => option.setAttribute('aria-selected', String(option.dataset.value === select.value)));
    },
    toggleAdaptiveSort() { if (this.sortOpen) this.closeAdaptiveSort(); else this.openAdaptiveSort(); },
    openAdaptiveSort(edge = 'selected') {
      const menu = this._root().querySelector('[data-sort-menu]');
      if (!menu) return;
      this.closeAdaptiveSheet(false);
      this.hideAdaptiveTooltip();
      this.sortOpen = true;
      menu.hidden = false;
      menu.inert = false;
      if (menu.showPopover && !menu.matches(':popover-open')) menu.showPopover();
      this._positionAdaptiveSort();
      const options = Array.from(menu.querySelectorAll('[data-sort-option]'));
      const selected = options.findIndex(option => option.dataset.value === this.currentSort);
      this._focusAdaptiveSort(edge === 'first' ? 0 : edge === 'last' ? options.length - 1 : Math.max(0, selected));
    },
    closeAdaptiveSort(restoreFocus = true) {
      const menu = this._root()?.querySelector('[data-sort-menu]');
      const wasOpen = this.sortOpen;
      this.sortOpen = false;
      if (menu) {
        if (menu.hidePopover && menu.matches(':popover-open')) menu.hidePopover();
        menu.hidden = true;
        menu.inert = true;
        menu.querySelectorAll('[data-sort-option]').forEach(option => { option.tabIndex = -1; });
      }
      if (wasOpen && restoreFocus) this._root().querySelector('[data-sort-trigger]')?.focus({ preventScroll: true });
    },
    _positionAdaptiveSort() {
      const menu = this._root().querySelector('[data-sort-menu]');
      const trigger = this._root().querySelector('[data-sort-trigger]');
      const rect = trigger.getBoundingClientRect();
      menu.style.width = `${Math.min(220, window.innerWidth - 24)}px`;
      menu.style.maxHeight = `${window.innerHeight - 24}px`;
      const below = window.innerHeight - rect.bottom - 20;
      const above = rect.top - 20;
      const flip = menu.scrollHeight > below && above > below;
      menu.style.maxHeight = `${Math.max(44, flip ? above : below)}px`;
      menu.style.left = `${Math.max(12, Math.min(window.innerWidth - menu.offsetWidth - 12, rect.right - menu.offsetWidth))}px`;
      menu.style.top = `${flip ? rect.top - 8 - menu.offsetHeight : rect.bottom + 8}px`;
    },
    _focusAdaptiveSort(index) {
      const options = Array.from(this._root().querySelectorAll('[data-sort-option]'));
      if (!options.length) return;
      options.forEach((option, position) => { option.tabIndex = position === index ? 0 : -1; });
      options[index]?.focus({ preventScroll: true });
      options[index]?.scrollIntoView({ block: 'nearest' });
    },
    handleAdaptiveSortKey(event) {
      if (event.key === 'Escape' && this.sortOpen) { event.preventDefault(); this.closeAdaptiveSort(); return; }
      if (event.key === 'Tab' && this.sortOpen) { this.closeAdaptiveSort(); return; }
      const arrows = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
      if (arrows.includes(event.key)) {
        event.preventDefault();
        if (!this.sortOpen) { this.openAdaptiveSort(event.key === 'ArrowUp' || event.key === 'End' ? 'last' : 'first'); return; }
        const options = Array.from(this._root().querySelectorAll('[data-sort-option]'));
        const index = Math.max(0, options.indexOf(document.activeElement));
        this._focusAdaptiveSort(event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length);
      } else if ((event.key === 'Enter' || event.key === ' ') && event.target.hasAttribute('data-sort-option')) {
        event.preventDefault();
        this.selectAdaptiveSort(event.target.dataset.value);
      }
    },
    selectAdaptiveSort(value) {
      const select = this._root().querySelector('[data-adaptive-sort]');
      if (!Array.from(select.options).some(option => option.value === value)) return;
      select.value = value;
      this._syncAdaptiveSort();
      this.closeAdaptiveSort();
      return this.submitAdaptive();
    },
    _setLayoutControlAvailability() {},
    _ensureActiveDesktopFilter() {},
    setColumns(value) {
      if (this.isDesktop) {
        this.desktopColumns = Math.max(2, Math.min(5, Math.round(Number(value)) || 4));
        save('product-grid-columns', this.desktopColumns);
      } else {
        this.mobileColumns = Math.max(1, Math.min(2, Math.round(Number(value)) || 2));
        save('product-grid-columns-mobile', this.mobileColumns);
      }
      this._applyGridColumns();
    },
    _applyGridColumns() {
      const grid = this._root()?.querySelector('#items-grid');
      if (grid) {
        grid.style.setProperty('--pg-columns-desktop', this.desktopColumns);
        grid.style.setProperty('--pg-columns-mobile', this.mobileColumns);
        const columns = this.isDesktop ? this.desktopColumns : this.mobileColumns;
        Array.from(grid.children).forEach(item => {
          // Read the theme's configured span once per breakpoint; never let a
          // promotional tile create implicit columns after density decreases.
          const key = this.isDesktop ? 'desktopSpan' : 'mobileSpan';
          item.style.removeProperty('grid-column');
          if (!item.dataset[key]) item.dataset[key] = getComputedStyle(item).gridColumnEnd.match(/span\s+(\d+)/)?.[1] || '1';
          const span = Math.min(columns, Number(item.dataset[key]));
          item.style.gridColumn = `span ${span} / span ${span}`;
        });
      }
      const slider = this._root()?.querySelector('.mwact-slider');
      if (slider) {
        const value = this.isDesktop ? this.desktopColumns : this.mobileColumns;
        slider.min = this.isDesktop ? 2 : 1;
        slider.max = this.isDesktop ? 5 : 2;
        slider.value = value;
        slider.style.setProperty('--act-progress', `${(value - Number(slider.min)) / (Number(slider.max) - Number(slider.min)) * 100}%`);
      }
    },
    _adaptiveScreens() { return Array.from(this._root().querySelectorAll('[data-filter-screen]')); },
    _quickInventoryFrom(root) {
      const total = Number(root.querySelector('[data-adaptive-toolbar]')?.dataset.allResultCount) || 0;
      return Array.from(root.querySelectorAll('[data-filter-label]')).map(screen => ({
        key: screen.dataset.filterScreen,
        label: screen.dataset.filterLabel,
        operator: screen.dataset.filterOperator,
        html: screen.outerHTML,
        values: Array.from(screen.querySelectorAll('[data-facet-option]')).map(input => ({
          value: input.value, label: input.dataset.valueLabel, count: Number(input.dataset.valueCount) || 0
        })),
        total
      }));
    },
    _quickFilterKeys() {
      const shell = this._root().querySelector('[data-adaptive-toolbar]');
      return {
        product_type: ['filter.p.product_type'],
        ingredient: [shell?.dataset.quickIngredient, 'filter.p.m.custom.ingredients', 'filter.p.m.custom.ingredient'],
        flavor: [shell?.dataset.quickFlavor, 'filter.p.m.custom.flavors', 'filter.p.m.custom.flavor', 'filter.v.option.flavor'],
        strain: [shell?.dataset.quickStrain, 'filter.p.m.custom.strain']
      };
    },
    _quickModeEnabled() {
      const mode = this._root().querySelector('[data-adaptive-toolbar]')?.dataset.quickMode || 'auto';
      // Saved single-category choices now enable the four-category menu.
      return ['auto', 'flavor', 'ingredient', 'product_type', 'strain', 'product_line', 'size', 'quantity'].includes(mode);
    },
    _chooseQuickFilters(inventory) {
      const total = Number(inventory[0]?.total ?? this._root().querySelector('[data-adaptive-toolbar]')?.dataset.allResultCount) || 0;
      if (!total || !this._quickModeEnabled()) return [];
      const keys = this._quickFilterKeys();
      const selected = [];
      for (const category of ['flavor', 'ingredient', 'product_type', 'strain']) {
        for (const key of [...new Set(keys[category].filter(Boolean))]) {
          const filter = inventory.find(item => item.key === key);
          const values = filter?.values.filter(value => {
            const label = String(value.label ?? value.value ?? '').trim();
            return label && !/^(option|default title)$/i.test(label) && value.count > 0;
          }) || [];
          if (!values.length || selected.some(item => item.key === key)) continue;
          selected.push({ ...filter, category, values });
          break;
        }
      }
      return selected;
    },
    async _initQuickFilters() {
      const shell = this._root().querySelector('[data-adaptive-toolbar]');
      if (!shell || shell.dataset.quickMode === 'off' || !shell.querySelector('[data-quick-entry]')) return;
      if (quickResolved) { this._setupQuickFilters(); return; }
      if (quickRequest) return;
      const query = new URLSearchParams(this.adaptivePendingParams ?? this.searchParamsPrev);
      const filtered = Array.from(query.keys()).some(key => this._isFilterParam(key));
      if (!filtered) {
        quickInventory = this._chooseQuickFilters(this._quickInventoryFrom(this._root()));
        quickResolved = true;
        this._setupQuickFilters();
        this._syncAdaptiveScreens();
        this.measureAdaptive();
        return;
      }
      quickRequest = new AbortController();
      const signal = quickRequest.signal;
      const abort = () => quickRequest?.abort();
      lifecycle?.signal.addEventListener('abort', abort, { once: true });
      try {
        this._deleteFilterParams(query);
        query.delete('page');
        query.set('section_id', sectionId);
        const response = await fetch(`${window.location.pathname}?${query}`, { signal });
        if (!response.ok) throw new Error(`Quick filter inventory ${response.status}`);
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (signal.aborted || !parsed.querySelector('[data-adaptive-form]')) return;
        quickInventory = this._chooseQuickFilters(this._quickInventoryFrom(parsed));
        quickResolved = true;
        this._setupQuickFilters();
        this._syncAdaptiveScreens();
        this.measureAdaptive();
      } catch (_) {
        // The standard filters remain usable. Retry inventory on the next result update.
      } finally {
        lifecycle?.signal.removeEventListener('abort', abort);
        quickRequest = null;
      }
    },
    _setupQuickFilters() {
      const shell = this._root().querySelector('[data-adaptive-toolbar]');
      const zone = shell?.querySelector('[data-adaptive-filters]');
      const home = zone?.querySelector('[data-filter-screen=""] .mwact-content');
      const all = zone?.querySelector('[data-all-categories]');
      const quick = zone?.querySelector('[data-quick-categories]');
      if (!home || !all || !quick) return;
      this.groupedFilters = quickResolved && this._quickModeEnabled();
      this.quickFilterKeys = this.groupedFilters ? quickInventory.map(filter => filter.key) : [];
      const params = new URLSearchParams(this.adaptivePendingParams ?? this.searchParamsPrev);
      // Restore omitted native groups and values from the full collection inventory.
      // Keep exactly one canonical input for every category/value pair.
      for (const [index, filter] of (this.groupedFilters ? quickInventory : []).entries()) {
        let canonical = Array.from(zone.querySelectorAll('[data-filter-label]')).find(screen => screen.dataset.filterScreen === filter.key);
        if (!canonical) {
          const template = document.createElement('template');
          template.innerHTML = filter.html;
          canonical = template.content.firstElementChild;
          const suffix = `-quick-restored-${index}`;
          const ids = new Map(Array.from(canonical.querySelectorAll('[id]')).concat(canonical).filter(element => element.id).map(element => [element.id, element.id + suffix]));
          for (const element of [canonical, ...canonical.querySelectorAll('*')]) {
            if (ids.has(element.id)) element.id = ids.get(element.id);
            for (const attr of ['aria-controls', 'aria-labelledby', 'for']) {
              if (element.hasAttribute(attr)) element.setAttribute(attr, element.getAttribute(attr).split(' ').map(id => ids.get(id) || id).join(' '));
            }
          }
          canonical.querySelectorAll('[data-facet-option]').forEach(input => {
            input.checked = params.getAll(filter.key).includes(input.value);
            input.disabled = !input.checked;
            input.dataset.valueCount = '0';
          });
          zone.append(canonical);
        }
        const content = canonical.querySelector('.mwact-content');
        for (const value of filter.values) {
          if (Array.from(content.querySelectorAll('[data-facet-option]')).some(input => input.value === value.value)) continue;
          const template = document.createElement('template');
          template.innerHTML = filter.html;
          const option = Array.from(template.content.querySelectorAll('[data-facet-option]')).find(input => input.value === value.value)?.closest('.mwact-option');
          if (!option) continue;
          const input = option.querySelector('input');
          input.checked = params.getAll(filter.key).includes(value.value);
          input.disabled = !input.checked;
          input.dataset.valueCount = '0';
          content.append(option);
        }
        // Keep the inventory's native order when Shopify omitted some values.
        for (const value of filter.values) {
          const input = Array.from(content.querySelectorAll('[data-facet-option]')).find(input => input.value === value.value);
          if (input) Alpine.mutateDom(() => content.append(input.closest('.mwact-option')));
        }
        // Non-data placeholders do not become selectable quick values.
        canonical.querySelectorAll('[data-facet-option]').forEach(input => {
          if (/^(option|default title)$/i.test(input.dataset.valueLabel?.trim()) && !input.checked) input.closest('.mwact-option').hidden = true;
        });
        if (!Array.from(zone.querySelectorAll('[data-standard-category]')).some(button => button.dataset.category === filter.key)) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'mwact-category';
          button.dataset.standardCategory = '';
          button.dataset.category = filter.key;
          button.dataset.focusKey = `category:${filter.key}`;
          button.setAttribute('aria-controls', canonical.id);
          button.setAttribute('aria-expanded', 'false');
          button.setAttribute('x-on:click', 'navigateAdaptive($el.dataset.category)');
          button.textContent = filter.label;
          const arrow = shell.querySelector('[data-quick-entry] .mwact-forward')?.cloneNode(true);
          if (arrow) button.append(arrow);
          quick.append(button);
        }
      }
      const nativeScreens = Array.from(zone.querySelectorAll('[data-filter-label]'));
      const quickScreens = this.quickFilterKeys.map(key => nativeScreens.find(screen => screen.dataset.filterScreen === key)).filter(Boolean);
      const moreScreens = nativeScreens.filter(screen => !this.quickFilterKeys.includes(screen.dataset.filterScreen));
      Alpine.mutateDom(() => {
        for (const button of zone.querySelectorAll('[data-standard-category]')) (this.groupedFilters ? (this.quickFilterKeys.includes(button.dataset.category) ? quick : all) : home).append(button);
        // Enforce menu order independently of Shopify's facet order.
        for (const key of this.quickFilterKeys) {
          const button = Array.from(quick.children).find(button => button.dataset.category === key);
          if (button) quick.append(button);
        }
        zone.append(shell.querySelector('[data-quick-heading]'), ...quickScreens, shell.querySelector('[data-all-heading]'), ...moreScreens);
      });
      const hasQuick = !!this.quickFilterKeys.length;
      const hasMore = this.groupedFilters && !!all.querySelector('[data-standard-category]');
      shell.querySelector('[data-quick-entry]').hidden = !hasQuick;
      shell.querySelector('[data-quick-heading]').hidden = !hasQuick;
      shell.querySelector('[data-all-entry]').hidden = !hasMore;
      shell.querySelector('[data-all-heading]').hidden = !hasMore;
      const selectedCount = screens => screens.reduce((count, screen) => count + screen.querySelectorAll('[data-facet-option]:checked').length + (screen.querySelector('[data-price-draft][data-applied]:not([data-applied=""])') ? 1 : 0), 0);
      const quickSelected = selectedCount(quickScreens);
      const moreSelected = selectedCount(moreScreens);
      this.appliedFilterCount = quickSelected + moreSelected;
      for (const [selector, count] of [['[data-quick-count]', quickSelected], ['[data-all-count]', moreSelected], ['[data-mobile-filter-count]', this.appliedFilterCount]]) {
        const badge = shell.querySelector(selector);
        if (badge) { badge.textContent = count; badge.hidden = count === 0; }
      }
    },
    adaptiveFilterParent(key) {
      return this.groupedFilters ? (this.quickFilterKeys.includes(key) ? '__quick' : '__all') : '';
    },
    _syncAdaptiveScreens() {
      const screens = this._adaptiveScreens();
      const available = screen => {
        const key = screen.dataset.filterScreen;
        if (key === '__quick') return !!this.quickFilterKeys.length;
        if (key === '__all') return this.groupedFilters && !this._root().querySelector('[data-all-entry]')?.hidden;
        return true;
      };
      if (!screens.some(screen => screen.dataset.filterScreen === this.activeAdaptiveFilter && available(screen))) this.activeAdaptiveFilter = '';
      screens.forEach(screen => {
        const key = screen.dataset.filterScreen;
        const active = available(screen) && (this.isDesktop ? key === this.activeAdaptiveFilter : key !== '' && key !== '__all' && key !== '__quick');
        screen.hidden = !active;
        screen.inert = !active;
        screen.setAttribute('aria-hidden', String(!active));
        delete screen.dataset.transitioning;
        const values = screen.querySelector('.mwact-values');
        const group = screen.querySelector('[data-mobile-group]');
        if (values) { values.hidden = !this.isDesktop && this.mobileGroups[key] === false; values.inert = values.hidden; }
        group?.setAttribute('aria-expanded', String(this.mobileGroups[key] !== false));
      });
      this._root().querySelectorAll('[data-category]').forEach(button => button.setAttribute('aria-expanded', String(button.dataset.category === this.activeAdaptiveFilter)));
    },
    navigateAdaptive(key) {
      if (!this.isDesktop) return;
      if (key === this.activeAdaptiveFilter) return;
      const screens = this._adaptiveScreens();
      const incoming = screens.find(screen => screen.dataset.filterScreen === key);
      if (!incoming) return;
      const outgoing = screens.find(screen => screen.dataset.filterScreen === this.activeAdaptiveFilter);
      const previousKey = this.activeAdaptiveFilter;
      const token = ++transition;
      animations.forEach(animation => animation.cancel());
      this.activeAdaptiveFilter = key;
      this._syncAdaptiveScreens();
      this.measureAdaptive();
      const finish = () => {
        if (token !== transition) return;
        this._syncAdaptiveScreens();
        const target = ['__quick', '__all'].includes(key) && previousKey.startsWith('filter.')
          ? Array.from(incoming.querySelectorAll('[data-category]')).find(button => button.dataset.category === previousKey)
          : key ? incoming.querySelector('.mwact-back') : Array.from(incoming.querySelectorAll('[data-category]')).find(button => button.dataset.category === previousKey);
        target?.focus({ preventScroll: true });
        if (target) this.revealAdaptiveControl({ target });
      };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !incoming.animate) { finish(); return; }
      outgoing.hidden = false;
      outgoing.inert = true;
      outgoing.setAttribute('aria-hidden', 'true');
      outgoing.dataset.transitioning = '';
      incoming.dataset.transitioning = '';
      const direction = !key || ['__quick', '__all'].includes(key) && previousKey.startsWith('filter.') ? -1 : 1;
      const timing = { duration: 260, easing: 'cubic-bezier(.22, 1, .36, 1)' };
      animations = [outgoing.animate([{ transform: 'translateY(0)' }, { transform: `translateY(${-direction * 100}%)` }], timing), incoming.animate([{ transform: `translateY(${direction * 100}%)` }, { transform: 'translateY(0)' }], timing)];
      Promise.all(animations.map(animation => animation.finished)).then(finish).catch(() => {});
    },
    measureAdaptive() {
      const zone = this._root().querySelector('[data-adaptive-filters]');
      const screen = this._adaptiveScreens().find(item => item.dataset.filterScreen === this.activeAdaptiveFilter);
      if (!zone || !screen || !this.isDesktop) return;
      const scroller = this._root().querySelector('[data-toolbar-scroll]');
      const slot = this._root().querySelector('[data-adaptive-filter-slot]');
      const persistent = Array.from(this._root().querySelector('[data-adaptive-form]').children).filter(item => item !== slot && getComputedStyle(item).position !== 'absolute').reduce((width, item) => width + item.getBoundingClientRect().width, 0);
      const style = getComputedStyle(screen);
      const back = screen.querySelector('.mwact-back');
      const intrinsic = screen.querySelector('.mwact-content').scrollWidth + parseFloat(style.paddingLeft) + parseFloat(style.paddingRight) + (back ? back.getBoundingClientRect().width + parseFloat(getComputedStyle(back).marginInlineEnd) : 0);
      slot.style.width = `${Math.max(0, Math.min(Math.ceil(intrinsic) + 1, scroller.clientWidth - persistent - 2))}px`;
    },
    revealAdaptiveControl(event) {
      // Scroll only the local horizontal containers; avoid moving the page.
      const target = event.target.closest('.mwact-option') || event.target;
      for (const scroller of this.isDesktop ? [target.closest('.mwact-values')] : []) {
        if (!scroller) continue;
        const bounds = scroller.getBoundingClientRect(), rect = target.getBoundingClientRect();
        if (rect.left < bounds.left) scroller.scrollLeft += rect.left - bounds.left - 4;
        else if (rect.right > bounds.right) scroller.scrollLeft += rect.right - bounds.right + 4;
      }
    },
    rememberAdaptiveDraft(input) {
      // Either boundary can resolve a cross-field range error.
      this._root().querySelectorAll('[data-price-draft]').forEach(field => { field.setCustomValidity(''); field.removeAttribute('aria-invalid'); });
      this.priceDrafts[input.name] = input.value;
    },
    _restoreAdaptiveDrafts() {
      const locale = this._root().querySelector('[data-adaptive-toolbar]').dataset.locale;
      const format = new Intl.NumberFormat(locale, { useGrouping: false, maximumFractionDigits: 20 });
      this._root().querySelectorAll('[data-price-draft]').forEach(input => {
        input.value = Object.prototype.hasOwnProperty.call(this.priceDrafts, input.name) ? this.priceDrafts[input.name] : (input.dataset.applied === '' ? '' : format.format(Number(input.dataset.applied)));
        input.placeholder = format.format(Number(input.getAttribute('placeholder')));
      });
    },
    _parseAdaptivePrice(value) {
      if (!value.trim()) return null;
      const locale = this._root().querySelector('[data-adaptive-toolbar]').dataset.locale;
      const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
      const group = parts.find(part => part.type === 'group')?.value;
      const decimal = parts.find(part => part.type === 'decimal')?.value || '.';
      let normalized = value.trim();
      if (group) normalized = normalized.split(group).join('');
      normalized = normalized.replace(/[\s\u00a0\u202f]/g, '').split(decimal).join('.');
      return /^\d+(\.\d+)?$/.test(normalized) ? Number(normalized) : NaN;
    },
    _adaptiveParams() {
      const params = new URLSearchParams(this.adaptivePendingParams ?? this.searchParamsPrev);
      const inputs = Array.from(this._root().querySelectorAll('[data-facet-option]'));
      new Set(inputs.map(input => input.name)).forEach(name => params.delete(name));
      inputs.filter(input => input.checked).forEach(input => params.append(input.name, input.value));
      const sort = this._root().querySelector('[data-adaptive-sort]');
      if (sort) params.set('sort_by', sort.value);
      params.delete('page');
      return params;
    },
    applyAdaptivePrice(event) {
      const range = event.target.closest('[data-price-range]') || this._root().querySelector('[data-price-range]');
      if (!range || !range.closest('[data-filter-screen]') || range.closest('[data-filter-screen]').hidden) return;
      const inputs = Array.from(range.querySelectorAll('[data-price-draft]'));
      const values = inputs.map(input => this._parseAdaptivePrice(input.value));
      const bound = Number(range.dataset.bound);
      let invalid = values.findIndex(value => value !== null && (!Number.isFinite(value) || value < 0 || value > bound));
      if (invalid < 0 && (values[0] ?? 0) > (values[1] ?? bound)) invalid = 1;
      inputs.forEach(input => { input.setCustomValidity(''); input.removeAttribute('aria-invalid'); });
      if (invalid >= 0) {
        const input = inputs[invalid];
        input.setAttribute('aria-invalid', 'true');
        input.setCustomValidity(this._root().querySelector('[data-adaptive-toolbar]').dataset.priceError);
        this.revealAdaptiveControl({ target: input });
        input.reportValidity();
        return;
      }
      const params = this._adaptiveParams();
      inputs.forEach((input, index) => {
        params.delete(input.name);
        if (values[index] !== null && !(index === 0 && values[index] === 0) && !(index === 1 && values[index] === bound)) params.set(input.name, String(values[index]));
        this.priceDrafts[input.name] = input.value;
      });
      this._renderPage(params.toString());
    },
    submitAdaptive() { return this._renderPage(this._adaptiveParams().toString()); },
    onSubmit() { return this.submitAdaptive(); },
    clearFilters() {
      const params = new URLSearchParams(this.adaptivePendingParams ?? this.searchParamsPrev);
      this._deleteFilterParams(params); params.delete('page');
      this.priceDrafts = {};
      this._root().querySelectorAll('[data-facet-option]').forEach(input => { input.checked = false; });
      if (this.isDesktop) this.navigateAdaptive(''); else this.activeAdaptiveFilter = '';
      return this._renderPage(params.toString());
    },
    retryAdaptive() { return this._renderPage(retryParams); },
    _startAdaptiveLoading() {
      clearTimeout(loadingTimer);
      this.showAdaptiveProgress = false;
      const grid = this._root().querySelector('#ProductGridContainer');
      grid?.setAttribute('aria-busy', 'true');
      // Preserve the original inline value across superseding requests.
      if (grid?.style && pendingGridHeight === null) {
        pendingGridHeight = grid.style.minHeight;
        grid.style.minHeight = `${grid.getBoundingClientRect().height}px`;
      }
      loadingTimer = setTimeout(() => { this.showAdaptiveProgress = true; }, 150);
    },
    _finishAdaptiveLoading() {
      clearTimeout(loadingTimer);
      this.showAdaptiveProgress = false;
      const grid = this._root().querySelector('#ProductGridContainer');
      grid?.setAttribute('aria-busy', 'false');
      if (grid?.style && pendingGridHeight !== null) grid.style.minHeight = pendingGridHeight;
      pendingGridHeight = null;
    },
    async _renderPage(params, updateURL = true) {
      // Repeated notifications for the same in-flight state must not abort and
      // replace one another (or mask a failed response with a duplicate request).
      if (this.loading && this.adaptivePendingParams === params) return;
      const token = ++sequence;
      request?.abort();
      request = new AbortController();
      retryParams = params;
      this.adaptivePendingParams = params;
      this.loading = true;
      this.adaptiveError = false;
      this._startAdaptiveLoading();
      if (!updateURL) this.priceDrafts = {};
      try {
        let html = this.cachedResults[params];
        if (!html) {
          const query = new URLSearchParams(params); query.set('section_id', sectionId);
          const response = await fetch(`${window.location.pathname}?${query}`, { signal: request.signal });
          if (!response.ok) throw new Error(`Collection response ${response.status}`);
          html = await response.text();
        }
        if (token !== sequence) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        if (!parsed.querySelector('#ProductGridContainer') || !parsed.querySelector('[data-adaptive-form]')) throw new Error('Incomplete collection response');
        this._renderResults(html);
        this.cachedResults[params] = html;
        lastHTML = html;
        this.searchParamsPrev = params;
        this.adaptivePendingParams = null;
        if (updateURL) this._updateURLHash(params);
        this._prepareHashPaginationLinks();
        document.dispatchEvent(new CustomEvent('eurus:collection:reloaded'));
      } catch (error) {
        if (token !== sequence || error.name === 'AbortError') return;
        this.adaptivePendingParams = null;
        if (lastHTML) this._restoreAdaptiveControls(lastHTML);
        this.adaptiveError = true;
        if (!updateURL) this._updateURLHash(this.searchParamsPrev, true);
      } finally {
        if (token === sequence) {
          this.loading = false;
          this._finishAdaptiveLoading();
        }
      }
    },
    _restoreAdaptiveControls(html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const zone = this._root().querySelector('[data-adaptive-filters]');
      const source = parsed.querySelector('[data-adaptive-filters]');
      const active = document.activeElement;
      const focusKey = zone?.contains(active) ? active.dataset.focusKey : null;
      const scrolls = new Map(this._adaptiveScreens().map(screen => [screen.dataset.filterScreen, screen.querySelector('.mwact-values')?.scrollLeft || 0]));
      const sheetBody = this._root().querySelector('[data-sheet-body]');
      const sheetScroll = sheetBody?.scrollTop || 0;
      ++transition;
      animations.forEach(animation => animation.cancel());
      if (source) {
        const destination = zone || source.cloneNode(false);
        if (!zone) this._root().querySelector('[data-adaptive-filter-slot]')?.append(destination);
        // Let Alpine's mutation observer clean up the old children and bind the
        // new ones. The theme's bundled Alpine does not expose destroyTree.
        destination.innerHTML = source.innerHTML;
        destination.hidden = false;
      } else if (zone) { zone.hidden = true; zone.innerHTML = ''; }
      if (!source && this.sheetOpen) this.closeAdaptiveSheet(false);
      const sort = this._root().querySelector('[data-adaptive-sort]');
      const incomingSort = parsed.querySelector('[data-adaptive-sort]');
      if (sort && incomingSort) sort.innerHTML = incomingSort.innerHTML;
      const sortOptions = this._root().querySelector('[data-adaptive-sort-options]');
      const incomingOptions = parsed.querySelector('[data-adaptive-sort-options]');
      if (sortOptions && incomingOptions) sortOptions.innerHTML = incomingOptions.innerHTML;
      const count = this._root().querySelector('[data-adaptive-count]');
      const incomingCount = parsed.querySelector('[data-adaptive-count]');
      if (count && incomingCount) count.innerHTML = incomingCount.innerHTML;
      const sheetCount = this._root().querySelector('[data-sheet-count]');
      const incomingSheetCount = parsed.querySelector('[data-sheet-count]');
      if (sheetCount && incomingSheetCount) sheetCount.innerHTML = incomingSheetCount.innerHTML;
      this._moveAdaptiveFilters();
      this._restoreAdaptiveDrafts();
      this._syncAdaptiveMetadata(parsed);
      this._initQuickFilters();
      this._syncAdaptiveScreens();
      this._syncAdaptiveSort();
      this.$nextTick(() => {
        this.measureAdaptive();
        this._adaptiveScreens().forEach(screen => { screen.querySelector('.mwact-values').scrollLeft = scrolls.get(screen.dataset.filterScreen) || 0; });
        if (sheetBody) sheetBody.scrollTop = sheetScroll;
        if (focusKey) {
          const target = Array.from(this._root().querySelectorAll('[data-focus-key]')).find(item => item.dataset.focusKey === focusKey && !item.closest('[hidden]')) || this._root().querySelector('[data-category]');
          target?.focus({ preventScroll: true });
        }
        if (this.sortOpen) this._positionAdaptiveSort();
      });
    },
    _renderResults(html) {
      this._restoreAdaptiveControls(html);
      this._renderProductGridContainer(html);
      this._applyGridColumns();
    },
    _prepareHashPaginationLinks() {
      if (!this.useHashFilterUrls) return;
      const state = this._hashParamsFromCombined(this.searchParamsPrev);
      this._root().querySelectorAll('#ProductGridContainer .pagination a[href]').forEach(link => {
        const target = new URL(link.getAttribute('href'), window.location.href);
        const params = new URLSearchParams(target.search);
        this._deleteHashParams(params);
        state.forEach((value, key) => params.append(key, value));
        link.setAttribute('href', this._visibleURL(params.toString()));
      });
    },
    showAdaptiveTooltip(anchor, dragging = false) {
      clearTimeout(tooltipTimer);
      tooltipAnchor = anchor;
      tooltipDragging = dragging;
      const show = () => {
        if (!anchor.isConnected) return;
        const shell = this._root().querySelector('[data-adaptive-toolbar]');
        const tip = shell.querySelector('[data-density-tooltip]');
        const slider = anchor.querySelector('input');
        const value = this.isDesktop ? this.desktopColumns : this.mobileColumns;
        const rect = anchor.getBoundingClientRect();
        tip.textContent = dragging ? `${value} ${shell.dataset.perRow}` : `${shell.dataset.layoutLabel}: ${value}\n${shell.dataset.layoutHelp}`;
        tip.hidden = false;
        tip.style.left = `${Math.max(tip.offsetWidth / 2 + 12, Math.min(window.innerWidth - tip.offsetWidth / 2 - 12, rect.left + rect.width / 2))}px`;
        tip.style.top = `${rect.top - 8}px`;
        tip.id = `AdaptiveDensityTip-${sectionId}`;
        slider?.setAttribute('aria-describedby', tip.id);
      };
      if (dragging) show(); else tooltipTimer = setTimeout(show, 400);
    },
    hideAdaptiveTooltip() {
      clearTimeout(tooltipTimer);
      const tip = this._root().querySelector('[data-density-tooltip]');
      if (tip) tip.hidden = true;
      this._root().querySelector('.mwact-slider')?.removeAttribute('aria-describedby');
    },
    endAdaptiveDrag() {
      if (!tooltipDragging) return;
      tooltipDragging = false;
      this.hideAdaptiveTooltip();
      if (tooltipAnchor?.matches(':hover, :focus-within')) this.showAdaptiveTooltip(tooltipAnchor);
    }
  };
};
